// Per spec/blocks.md §15. Compliance footer — always the last block.
// Composes Image (chapter logo) + Text (physical address + recipient line)
// + Links (Unsubscribe + Constant Contact Data Notice) + a 10 px navy
// bottom bar.
//
// CC VARIABLE TAGS: `[[EmailAddress]]` and `[[unsubscribe]]` are LITERAL
// text in the rendered HTML — Constant Contact substitutes them at send
// time. The kernel's `escapeHtml` only escapes `& < > " '` (D-021 +
// D-024) — `[` and `]` pass through both as literal template positions
// AND as Link primitive `href` interpolation. The unsubscribe tag rides
// through the Link primitive's href, which means we get D-030's secure
// `rel="noopener noreferrer"` default automatically.
//
// **Tag spellings verified against CC v3 docs (P-048 follow-up).** Per
// the v3 API Custom Code design guide + CC's "Using variable tags in
// content editors" KB article, the canonical bracket-form tags are
// `[[EmailAddress]]` (PascalCase, no underscore — NOT `[[Email_Address]]`)
// and `[[unsubscribe]]` (lowercase, no `Link` suffix — NOT
// `[[UnsubscribeLink]]`). First real Push at P-048 close exposed the
// pre-P-048 best-guess spellings rendering literal in CC's editor (raw
// `[[Email_Address]]` body text + CC's CTA tracker wrapping
// `[[UnsubscribeLink]]` as a path component in `app.constantcontact.com/
// pages/ace/v1/[[UnsubscribeLink]]` instead of substituting). Swap-fix
// shipped immediately post-P-048.
//
// **P-037.18 D-118.**
// - `theme` enum dropped — single light look (was light/dark per P-012;
//   D-100 had already removed dark-mode infra). Schema v3 → v4 migration
//   strips the field from persisted state.
// - Background color: was `tokens.color['rule-subtle']` until P-037.23
//   (see next entry).
// - 10 px solid bar across the bottom in `tokens.color['accent-navy-
//   deep']` (#041e42 — same hex used for ≥18 px headlines).
// - Default `physicalAddress` updated to chapter HQ ("3423 Piedmont Road
//   NE, Atlanta, GA 30305 US").
// - "Update preferences · " link removed entirely (Banks's audit: drop
//   the [[UpdateProfileLink]] tag). CTA line is now
//   "Unsubscribe · Constant Contact Data Notice".
// - "Constant Contact Data Notice" link added (static URL — no CC tag
//   substitution needed).
// - Link color reads `ctx.linkColor` (template-level derivation from
//   Header style — D-118 Request 2). Default falls back to
//   `tokens.color['link-default']`.
//
// **P-037.23 D-131.**
// - Background swaps from the generic `tokens.color['rule-subtle']`
//   (#e5e5e5) to `ctx.tintColor` — the 10% blend of the per-style link
//   color over white, derived from the Email's first included Header
//   block (same first-Header-wins walk as D-118 linkColor). The footer's
//   bg now visually belongs to the same per-Email family as the rest of
//   the rendered email (gold for Signal, blue for State Event, etc.).
//   Pre-computed hex per Banks-confirmed mapping (`opacity` / `rgba()`
//   are dropped by Outlook desktop's Word rendering engine).
// - Chapter name "YPO Southern 7" text → logo image
//   (`/ypo-s7.png`, 250 × 42, centered, native source dimensions). The
//   prior `<h3>` chapter-name heading is gone; the IMG's `alt="YPO
//   Southern 7"` preserves the chapter name for screen readers + image-
//   blocked clients. Native-display matches the `/ypo-s7-email-
//   subheader-*.png` precedent.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { image } from '../primitives/image'
import { link } from '../primitives/link'
import { HtmlString } from '../types'

// P-037.18 D-118: theme enum dropped; options-free schema for uniform
// BlockDefinition shape (the form generator iterates optionsSchema
// fields; empty object is fine).
const optionsSchema = z.object({})

const inputsSchema = z.object({
  physicalAddress: z.string().min(1),
})

const PAD_X = tokens.space[6]
const PAD_TOP = tokens.space[8]
const PAD_BOTTOM = tokens.space[8]
const META_GAP = tokens.space[2]
// P-037.25 D-133 — extra breathing room between the chapter logo row
// and the address row (~+20 px on top of the META_GAP rhythm). The
// other inter-row gaps (address → recipient, recipient → CTA) keep
// `META_GAP` so the meta-text block stays tight.
const LOGO_TO_ADDRESS_GAP = META_GAP + 20
const BOTTOM_BAR_HEIGHT = 10
const LOGO_WIDTH = 250
const LOGO_HEIGHT = 42
const CC_DATA_NOTICE_URL = 'https://www.constantcontact.com/legal/customer-contact-data-notice'

const RECIPIENT_LINE = html`This email was sent to [[EmailAddress]]`

function centerRow(content: HtmlString, padBottom: number): HtmlString {
  // prettier-ignore
  return html`<tr><td align="center" style="padding: 0 ${PAD_X}px ${padBottom}px;">${content}</td></tr>`
}

export const footer = defineBlock({
  id: 'footer',
  label: 'Footer',
  category: 'footer',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: {
    physicalAddress: '3423 Piedmont Road NE, Atlanta, GA 30305 US',
  },
  render: ({ inputs }, ctx) => {
    const bg = ctx.tintColor
    const fg = tokens.color['text-secondary']
    const barBg = tokens.color['accent-navy-deep']
    const linkColor = ctx.linkColor

    const logoHtml = image({
      src: '/ypo-s7.png',
      alt: 'YPO Southern 7',
      width: LOGO_WIDTH,
      height: LOGO_HEIGHT,
      align: 'center',
    })
    const addressHtml = text({
      text: inputs.physicalAddress,
      size: tokens.type.size.sm,
      color: fg,
      align: 'center',
    })
    const recipientHtml = text({
      text: RECIPIENT_LINE,
      size: tokens.type.size.sm,
      color: fg,
      align: 'center',
    })
    // P-037.18 D-118: CTA line drops "Update preferences · " entirely and
    // adds the Constant Contact Data Notice as a static-URL link. Both
    // links use ctx.linkColor.
    const unsubscribeLink = link({
      text: 'Unsubscribe',
      href: '[[unsubscribe]]',
      color: linkColor,
    })
    const dataNoticeLink = link({
      text: 'Constant Contact Data Notice',
      href: CC_DATA_NOTICE_URL,
      color: linkColor,
    })
    // prettier-ignore
    const ctaLine = html`${unsubscribeLink} &middot; ${dataNoticeLink}`
    const ctaHtml = text({
      text: ctaLine,
      size: tokens.type.size.sm,
      color: fg,
      align: 'center',
    })

    // 10 px solid bar across the full footer width. `font-size: 0` +
    // `line-height: 0` collapse the `&nbsp;` content to nothing so the
    // row's height is exactly `BOTTOM_BAR_HEIGHT`. P-048.1 D-144 (Item 5):
    // `mso-line-height-rule: exactly` added so Outlook desktop's Word
    // renderer honors the 0 line-height — without it, the bar can
    // visibly inflate past 10 px.
    // prettier-ignore
    const bottomBar = html`<tr><td height="${BOTTOM_BAR_HEIGHT}" bgcolor="${barBg}" style="background-color: ${barBg}; height: ${BOTTOM_BAR_HEIGHT}px; line-height: 0; font-size: 0; mso-line-height-rule: exactly;">&nbsp;</td></tr>`

    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" bgcolor="${bg}" style="background-color: ${bg};"><tr><td align="center" style="padding: ${PAD_TOP}px ${PAD_X}px ${LOGO_TO_ADDRESS_GAP}px;">${logoHtml}</td></tr>${centerRow(addressHtml, META_GAP)}${centerRow(recipientHtml, META_GAP)}${centerRow(ctaHtml, PAD_BOTTOM)}${bottomBar}</table>`
  },
})
